import React, {createContext, useContext, useMemo} from 'react';

import type * as PanelTypes from '../../../../state/views/panel/types';
import {type Ref as SectionRef} from '../../../../state/views/panelBankSectionConfig/types';
import {type MarimoConfig} from '../../../PanelMarimo/common';
import {usePanelSettingsConfigUpdates} from '../hooks/usePanelSettingsConfigUpdates';

type MarimoSettingsStateContextValue = {
  config: MarimoConfig;
  updateConfig: (update: Partial<MarimoConfig>) => void;
  unsetConfigField: (field: keyof MarimoConfig) => void;
  panelId: string;
  viewID: string;
};

const MarimoSettingsStateContext =
  createContext<MarimoSettingsStateContextValue | null>(null);

type MarimoSettingsStateContextProviderProps = {
  children: React.ReactNode;
  panelRef: PanelTypes.Ref;
  panelBankSectionConfigRef: SectionRef;
};

export function MarimoSettingsStateContextProvider({
  children,
  panelRef,
  panelBankSectionConfigRef,
}: MarimoSettingsStateContextProviderProps) {
  const value = usePanelSettingsConfigUpdates<MarimoConfig, 'Marimo'>({
    panelRef,
    panelBankSectionConfigRef,
  });
  const contextValue = useMemo(
    () => ({...value, viewID: panelRef.viewID}),
    [value, panelRef.viewID]
  );
  return (
    <MarimoSettingsStateContext.Provider value={contextValue}>
      {children}
    </MarimoSettingsStateContext.Provider>
  );
}

export function useMarimoSettingsStateContext(): MarimoSettingsStateContextValue {
  const context = useContext(MarimoSettingsStateContext);
  if (context === null) {
    throw new Error(
      'useMarimoSettingsStateContext must be used within a MarimoSettingsStateContextProvider'
    );
  }
  return context;
}
